import React, { useState, useEffect, useCallback } from "react";
import withErrorHandling from "../HOC/ErrorHandling";
import { format } from 'date-fns';
import {
  Box, Typography, ToggleButtonGroup, ToggleButton,
  IconButton, Tooltip, useTheme, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { LineChart } from '@mui/x-charts/LineChart';
import { getEnv } from "../helpers/env";
import Loading from "../components/Loading";

const AGENTS_URL =
  getEnv("REACT_APP_AGENTS_URL") ||
  "https://clue2app-agents.control.apps.clue2.app";

const TIME_RANGES = [
  { id: "1h", label: "1H", hours: 1 },
  { id: "6h", label: "6H", hours: 6 },
  { id: "12h", label: "12H", hours: 12 },
  { id: "24h", label: "24H", hours: 24 },
];

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(3, Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
}

function formatMB(bytes) {
  if (!bytes) return 0;
  return bytes / (1024 * 1024);
}

const Metrics = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("1h");
  const [metricsData, setMetricsData] = useState(null);
  const [error, setError] = useState(null);

  const fetchMetrics = useCallback(async () => {
    const range = TIME_RANGES.find(t => t.id === timeRange) || TIME_RANGES[0];
    try {
      const response = await fetch(`${AGENTS_URL}/costs/metrics-history?hours=${range.hours}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setMetricsData(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch metrics:", err);
      setError(err.message);
    }
  }, [timeRange]);

  useEffect(() => {
    setIsLoading(true);
    fetchMetrics().finally(() => setIsLoading(false));
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // Transform data: convert memory to MB for readable axis
  const chartData = (metricsData?.data || []).map(point => ({
    timestamp: new Date(point.timestamp).getTime(),
    cpu: point.total_cpu * 1000,
    memoryMB: formatMB(point.total_memory),
    ccu: point.total_ccu,
    pods: point.pods,
  }));

  const latestPoint = chartData.length > 0 ? chartData[chartData.length - 1] : null;
  const latestServices = metricsData?.data?.length > 0
    ? metricsData.data[metricsData.data.length - 1].services
    : [];

  function timeFormatter(value) {
    if (!value) return "";
    const date = new Date(value);
    const range = TIME_RANGES.find(t => t.id === timeRange);
    if (range && range.hours <= 6) return format(date, 'HH:mm');
    return format(date, 'HH:mm');
  }

  const chartSx = {
    '& .MuiChartsAxis-label': { fill: isDark ? '#94a3b8' : '#64748b', fontSize: 12 },
    '& .MuiChartsAxis-tickLabel': { fill: isDark ? '#94a3b8' : '#64748b' },
    '& .MuiChartsGrid-line': { stroke: isDark ? '#1e293b' : '#f1f5f9' },
    '& .MuiAreaElement-root': { fillOpacity: 0.08 },
    '& .MuiLineElement-root': { strokeWidth: 2 },
    '& .MuiMarkElement-root': { display: 'none' },
  };

  const panelSx = {
    p: 2, borderRadius: 2, mb: 2,
    bgcolor: isDark ? 'background.paper' : '#fff',
    border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
          Resource Metrics
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(_, val) => val && setTimeRange(val)}
          size="small"
          sx={{ mr: 1 }}
        >
          {TIME_RANGES.map(r => (
            <ToggleButton key={r.id} value={r.id} sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => { setIsLoading(true); fetchMetrics().finally(() => setIsLoading(false)); }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {isLoading ? (
        <Loading />
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="error" variant="body2">Failed to load metrics: {error}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Data appears after the agent collects its first snapshot (~5 min).
          </Typography>
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary" variant="body2">No metrics data available yet.</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Metrics are collected every 5 minutes. Check back shortly.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Summary row */}
          {latestPoint && (
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              {[
                { label: 'CPU', value: `${latestPoint.cpu.toFixed(0)}m`, color: '#F97316' },
                { label: 'Memory', value: `${latestPoint.memoryMB.toFixed(0)} MB`, color: '#8b5cf6' },
                { label: 'CCU', value: latestPoint.ccu.toFixed(1), color: '#06b6d4' },
                { label: 'Pods', value: latestPoint.pods, color: '#10b981' },
              ].map(stat => (
                <Box key={stat.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: stat.color }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* CPU + Memory side by side */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Box sx={panelSx}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                CPU Usage
              </Typography>
              <LineChart
                xAxis={[{ dataKey: 'timestamp', scaleType: 'time', valueFormatter: timeFormatter }]}
                yAxis={[{ label: 'millicores', min: 0 }]}
                series={[{
                  dataKey: 'cpu',
                  color: '#F97316',
                  area: true,
                  showMark: false,
                  valueFormatter: (v) => `${v?.toFixed(1)}m`,
                }]}
                dataset={chartData}
                height={220}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>

            <Box sx={panelSx}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                Memory Usage
              </Typography>
              <LineChart
                xAxis={[{ dataKey: 'timestamp', scaleType: 'time', valueFormatter: timeFormatter }]}
                yAxis={[{ label: 'MB', min: 0 }]}
                series={[{
                  dataKey: 'memoryMB',
                  color: '#8b5cf6',
                  area: true,
                  showMark: false,
                  valueFormatter: (v) => `${v?.toFixed(0)} MB`,
                }]}
                dataset={chartData}
                height={220}
                grid={{ horizontal: true }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>
          </Box>

          {/* CCU full width */}
          <Box sx={panelSx}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
              CCU (Cluster Compute Units)
            </Typography>
            <LineChart
              xAxis={[{ dataKey: 'timestamp', scaleType: 'time', valueFormatter: timeFormatter }]}
              yAxis={[{ min: 0 }]}
              series={[{
                dataKey: 'ccu',
                color: '#06b6d4',
                area: true,
                showMark: false,
                valueFormatter: (v) => `${v?.toFixed(2)} CCU`,
              }]}
              dataset={chartData}
              height={200}
              grid={{ horizontal: true }}
              slotProps={{ legend: { hidden: true } }}
              sx={chartSx}
            />
          </Box>

          {/* Per-service table */}
          {latestServices.filter(s => s.pods > 0).length > 0 && (
            <Paper sx={{ ...panelSx, p: 0, overflow: 'hidden' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} sx={{ px: 2, pt: 2, display: 'block' }}>
                Per-Service Breakdown
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderColor: isDark ? '#1e293b' : undefined }}>Service</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderColor: isDark ? '#1e293b' : undefined }}>CPU</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderColor: isDark ? '#1e293b' : undefined }}>Memory</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderColor: isDark ? '#1e293b' : undefined }}>CCU</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderColor: isDark ? '#1e293b' : undefined }}>Pods</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {latestServices
                      .filter(s => s.pods > 0)
                      .sort((a, b) => b.ccu - a.ccu)
                      .map(svc => (
                        <TableRow key={svc.name} hover>
                          <TableCell sx={{ borderColor: isDark ? '#1e293b' : undefined }}>
                            <Typography variant="body2" fontWeight={500}>{svc.name}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderColor: isDark ? '#1e293b' : undefined }}>
                            <Typography variant="body2" sx={{ color: '#F97316' }}>{(svc.cpu * 1000).toFixed(0)}m</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderColor: isDark ? '#1e293b' : undefined }}>
                            <Typography variant="body2" sx={{ color: '#8b5cf6' }}>{formatBytes(svc.memory)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderColor: isDark ? '#1e293b' : undefined }}>
                            <Typography variant="body2" sx={{ color: '#06b6d4' }}>{svc.ccu.toFixed(1)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ borderColor: isDark ? '#1e293b' : undefined }}>
                            <Typography variant="body2" color="text.secondary">{svc.pods}</Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default withErrorHandling(Metrics);
